import styled, { css } from "styled-components";
import { NavLink } from "react-router-dom";

export const Tooltip = styled.span`
  position: fixed;
  transform: translateY(-50%);
  background: ${({ theme }) => (theme.light ? "#ffffff" : "#1a1a1a")};
  border: 1px solid rgba(${({ theme }) => theme.accentRgb}, 0.3);
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 6px 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 9999;
  border-radius: 4px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s ease;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavItem = styled.div`
  position: relative;
`;

export const AlertDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff4444;
  position: absolute;
  top: 8px;
  right: 8px;
  pointer-events: none;
  box-shadow: 0 0 6px rgba(255, 68, 68, 0.8);
  animation: alertBlink 2.5s ease-in-out infinite;

  @keyframes alertBlink {
    0%,
    100% {
      opacity: 1;
      box-shadow: 0 0 6px rgba(255, 68, 68, 0.8);
    }
    50% {
      opacity: 0.5;
      box-shadow: 0 0 2px rgba(255, 68, 68, 0.3);
    }
  }
`;

export const CollapseBtn = styled.button`
  position: absolute;
  right: -12px;
  bottom: calc(75px + 16px);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => (theme.light ? "#ffffff" : "#1a1a1a")};
  border: 1px solid ${({ theme }) => theme.cardBorder};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 101;
  transition:
    border-color 0.2s,
    color 0.2s,
    background 0.2s;
  padding: 0;

  &:hover {
    border-color: rgba(${({ theme }) => theme.accentRgb}, 0.6);
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => (theme.light ? "#f0f0f0" : "#222")};
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const Sidebar = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ $collapsed }) => ($collapsed ? "64px" : "240px")};
  background: ${({ theme }) => (theme.light ? "#ffffff" : "#111")};
  border-right: 1px solid ${({ theme }) => theme.cardBorder};
  display: flex;
  flex-direction: column;
  z-index: 100;
  transition: width 0.25s ease;
  will-change: width;
  overflow: visible;

  @media (max-width: 1024px) {
    width: 64px;
  }

  @media (max-width: 768px) {
    width: 260px;
    transform: translateX(${({ $open }) => ($open ? "0" : "-100%")});
    transition:
      width 0.25s ease,
      transform 0.3s ease;
  }
`;

export const SidebarOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 99;
  }
`;

export const SidebarBrand = styled.div`
  padding: ${({ $collapsed }) => ($collapsed ? "24px 16px" : "24px 20px")};
  display: flex;
  align-items: center;
  gap: ${({ $collapsed }) => ($collapsed ? "0" : "12px")};
  border-bottom: 1px solid ${({ theme }) => theme.cardBorder};
  overflow: hidden;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? "center" : "flex-start"};
  transition:
    padding 0.25s ease,
    gap 0.25s ease;

  img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    flex-shrink: 0;
    ${({ theme }) => theme.light && "filter: invert(1);"}
  }

  span {
    font-family: ${({ theme }) => theme.font};
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 2px;
    white-space: nowrap;
    overflow: hidden;
    max-width: ${({ $collapsed }) => ($collapsed ? "0" : "200px")};
    opacity: ${({ $collapsed }) => ($collapsed ? "0" : "1")};
    transition: ${({ $collapsed }) =>
      $collapsed
        ? "max-width 0.25s ease, opacity 0.1s ease, margin 0.25s ease"
        : "max-width 0.25s ease, opacity 0.2s ease 0.15s, margin 0.25s ease"};
  }

  @media (max-width: 1024px) {
    padding: 24px 16px;
    justify-content: center;
    gap: 0;
    span {
      max-width: 0;
      opacity: 0;
    }
  }
  @media (max-width: 768px) {
    padding: 24px 20px;
    justify-content: flex-start;
    gap: 12px;
    span {
      max-width: 200px;
      opacity: 1;
    }
  }
`;

export const SidebarNav = styled.nav`
  flex: 1;
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const activeLinkStyles = css`
  color: ${({ theme }) => theme.accent};
  background: rgba(${({ theme }) => theme.accentRgb}, 0.06);
  border-left: 3px solid ${({ theme }) => theme.accent};
`;

const collapsedLinkStyles = css`
  justify-content: center;
  padding: 12px;
  border-left: none !important;
  border-radius: 8px;
  gap: 0;
  &.active {
    border-left: none !important;
    border-radius: 8px;
  }
  span {
    max-width: 0;
    opacity: 0;
    margin: 0;
    transition:
      max-width 0.25s ease,
      opacity 0.1s ease,
      margin 0.25s ease;
  }
`;

export const SidebarLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: ${({ theme }) => (theme.light ? "#666666" : theme.textSecondary)};
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  border-left: 3px solid transparent;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    padding 0.25s ease,
    border-radius 0.25s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.cardBg};
  }

  &.active {
    ${activeLinkStyles}
  }

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    stroke-width: 2;
  }

  span {
    overflow: hidden;
    white-space: nowrap;
    max-width: 200px;
    opacity: 1;
    transition:
      max-width 0.25s ease,
      opacity 0.2s ease 0.15s,
      margin 0.25s ease;
  }

  ${({ $collapsed }) => $collapsed && collapsedLinkStyles}

  @media (max-width: 1024px) {
    ${collapsedLinkStyles}
  }
  @media (max-width: 768px) {
    justify-content: flex-start;
    padding: 12px 16px;
    border-left: 3px solid transparent;
    border-radius: 0;
    &.active {
      border-left: 3px solid ${({ theme }) => theme.accent};
      border-radius: 0;
    }
    span {
      max-width: 200px;
      opacity: 1;
      transition:
        max-width 0.25s ease,
        opacity 0.2s ease 0.15s,
        margin 0.25s ease;
    }
  }
`;

export const SidebarBottom = styled.div`
  padding: 16px 8px;
  border-top: 1px solid ${({ theme }) => theme.cardBorder};
  overflow: hidden;
`;

export const LogoutBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: none;
  border: none;
  color: ${({ theme }) => (theme.light ? "#666666" : theme.textSecondary)};
  font-family: ${({ theme }) => theme.font};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.2s,
    padding 0.25s ease;

  &:hover {
    color: #ff4d4d;
  }

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }

  span {
    overflow: hidden;
    white-space: nowrap;
    max-width: 200px;
    opacity: 1;
    transition:
      max-width 0.25s ease,
      opacity 0.2s ease 0.15s,
      margin 0.25s ease;
  }

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      padding: 12px;
      span {
        max-width: 0;
        opacity: 0;
        margin: 0;
        transition:
          max-width 0.25s ease,
          opacity 0.1s ease,
          margin 0.25s ease;
      }
    `}

  @media (max-width: 1024px) {
    justify-content: center;
    padding: 12px;
    span {
      max-width: 0;
      opacity: 0;
      margin: 0;
      transition:
        max-width 0.25s ease,
        opacity 0.1s ease,
        margin 0.25s ease;
    }
  }
  @media (max-width: 768px) {
    justify-content: flex-start;
    padding: 12px 16px;
    span {
      max-width: 200px;
      opacity: 1;
      transition:
        max-width 0.25s ease,
        opacity 0.2s ease 0.15s,
        margin 0.25s ease;
    }
  }
`;

export const TopBar = styled.header`
  position: sticky;
  top: 0;
  height: 64px;
  background: ${({ theme }) =>
    theme.light ? "rgba(255,255,255,0.85)" : "rgba(13,13,13,0.85)"};
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ theme }) => theme.cardBorder};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  z-index: 50;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

export const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Hamburger = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  padding: 4px;
  @media (max-width: 768px) {
    display: flex;
  }
`;

export const PageTitle = styled.h1`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.3px;
`;

export const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const AdminEmail = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.textSecondary};
  @media (max-width: 480px) {
    display: none;
  }
`;

export const Avatar = styled.div`
  width: 34px;
  height: 34px;
  background: rgba(${({ theme }) => theme.accentRgb}, 0.12);
  border: 1px solid rgba(${({ theme }) => theme.accentRgb}, 0.25);
  color: ${({ theme }) => theme.accent};
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  text-transform: uppercase;
  flex-shrink: 0;
`;

export const Layout = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.textPrimary};
`;

export const Main = styled.main`
  margin-left: ${({ $collapsed }) => ($collapsed ? "64px" : "240px")};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.25s ease;

  @media (max-width: 1024px) {
    margin-left: 64px;
  }
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

export const Content = styled.div`
  flex: 1;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;
